import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Card,
  Space,
  message,
  Badge,
  Tabs,
  Table,
  Empty,
  Collapse,
} from "antd";

import { GetCMSDoc, UpdateCMSDoc, VerifyCrewTimerID } from "../../APIManager";
import { useRegattaSocket } from "../../hooks/useRegattaSocket";

const SYNC_MODE_OPTIONS = [
  { label: "Manual", value: "Manual" },
  { label: "Polling", value: "Polling" },
  { label: "RealTime", value: "RealTime" },
];

export default function CrewTimerConfig() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [idVerified, setIdVerified] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [config, setConfig] = useState({});
  const [testLoading, setTestLoading] = useState(false);
  const [testData, setTestData] = useState(null);
  const [selectedEventIdx, setSelectedEventIdx] = useState(null);
  const [liveMessages, setLiveMessages] = useState([]);
  const [form] = Form.useForm();
  const { data: liveData, isConnected, error: socketError } = useRegattaSocket(
    config.CrewTimerID || ""
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const resConfig = await GetCMSDoc("CrewTimerConfig");
      setConfig(resConfig.data.Data || {});
    } catch (err) {
      console.error(err);
      message.error("Failed to load CrewTimer config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (config && Object.keys(config).length) {
      form.setFieldsValue({
        CrewTimerID: config.CrewTimerID || "",
        SyncMode: config.SyncMode || "Manual",
        PollingFrequency: config.PollingFrequency ?? 0,
      });
      setIdVerified(true);
      setVerificationAttempted(true);
    }
  }, [config, form]);

  useEffect(() => {
    if (liveData) {
      const timestamp = new Date().toLocaleTimeString();
      setLiveMessages((prev) => [
        {
          id: Date.now(),
          timestamp,
          data: liveData,
        },
        ...prev,
      ]);
    }
  }, [liveData]);

  const handleVerifyID = async () => {
    const crewTimerID = form.getFieldValue("CrewTimerID");
    if (!crewTimerID || !crewTimerID.trim()) {
      message.error("Please enter CrewTimer ID first");
      return;
    }

    setVerifying(true);
    try {
      const res = await VerifyCrewTimerID(crewTimerID.trim());
      if (res.data.valid) {
        message.success("CrewTimer ID verified successfully");
        setIdVerified(true);
        setVerificationAttempted(true);
      } else {
        message.error("CrewTimer ID is invalid");
        setIdVerified(false);
        setVerificationAttempted(true);
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to verify CrewTimer ID");
      setIdVerified(false);
      setVerificationAttempted(true);
    } finally {
      setVerifying(false);
    }
  };

  const handleSave = async (vals) => {
    const data = {
      CrewTimerID: vals.CrewTimerID?.trim(),
      SyncMode: vals.SyncMode,
      PollingFrequency: vals.SyncMode === "Polling" ? vals.PollingFrequency : 0,
    };

    setSaving(true);
    try {
      await UpdateCMSDoc({
        docname: "CrewTimerConfig",
        data,
      });
      message.success("CrewTimer config saved successfully");
      setConfig(data);
    } catch (err) {
      console.error(err);
      message.error("Failed to save CrewTimer config");
    } finally {
      setSaving(false);
    }
  };

  const handleFetchTestData = async () => {
    const crewTimerID = form.getFieldValue("CrewTimerID");
    if (!crewTimerID || !crewTimerID.trim()) {
      message.error("Please enter CrewTimer ID first");
      return;
    }

    setTestLoading(true);
    try {
      const res = await VerifyCrewTimerID(crewTimerID.trim());
      if (res.data.valid) {
        setTestData(res.data);
        message.success("Test data fetched successfully");
      } else {
        message.error("CrewTimer ID is invalid");
        setTestData(null);
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch test data");
      setTestData(null);
    } finally {
      setTestLoading(false);
    }
  };

  if (loading) {
    return (
      <Card title="CrewTimer Config" className="p-4 rounded-2xl shadow-sm">
        Loading...
      </Card>
    );
  }

  return (
    <div>
      <Card
        title="CrewTimer Config"
        className="p-4 rounded-2xl shadow-sm"
        style={{ marginBottom: 24 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            label="CrewTimer ID"
            name="CrewTimerID"
            rules={[
              { required: true, message: "Please enter CrewTimer ID" },
              { whitespace: true, message: "CrewTimer ID cannot be empty" },
            ]}
          >
            <Input
              placeholder="Enter CrewTimer ID"
              onChange={() => setVerificationAttempted(false)}
            />
          </Form.Item>
          {verificationAttempted && (
            <div style={{ marginBottom: 16 }}>
              <Badge
                status={idVerified ? "success" : "error"}
                text={idVerified ? "Valid ID" : "Invalid ID"}
              />
            </div>
          )}
          <Button
            onClick={handleVerifyID}
            loading={verifying}
            style={{ marginBottom: 16 }}
          >
            Verify ID
          </Button>

          <Form.Item
            label="Sync Mode"
            name="SyncMode"
            rules={[{ required: true, message: "Please select sync mode" }]}
          >
            <Select options={SYNC_MODE_OPTIONS} />
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, curr) => prev.SyncMode !== curr.SyncMode}
          >
            {({ getFieldValue }) =>
              getFieldValue("SyncMode") === "Polling" ? (
                <Form.Item
                  label="Polling Frequency"
                  name="PollingFrequency"
                  rules={[
                    {
                      required: true,
                      message: "Please enter polling frequency",
                    },
                    {
                      type: "number",
                      min: 1,
                      message: "Value must be at least 1",
                    },
                  ]}
                  extra="Units are in minutes"
                >
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Space style={{ marginTop: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              disabled={!idVerified}
            >
              Save Config
            </Button>
          </Space>
        </Form>
      </Card>

      <Card
        title="Manual Test"
        className="p-4 rounded-2xl shadow-sm"
        style={{ marginTop: 24 }}
      >
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            onClick={handleFetchTestData}
            loading={testLoading}
          >
            Fetch Test Data
          </Button>
        </Space>

        {testData ? (
          testData.valid ? (
            <div>
              <Badge
                status="success"
                text="Data Fetched Successfully"
                style={{ marginBottom: 16, display: "block" }}
              />
              {testData.results && testData.results.length > 0 ? (
                <>
                  <Form.Item label="Select Event" style={{ marginBottom: 16 }}>
                    <Select
                      showSearch
                      placeholder="Search and select an event"
                      optionFilterProp="label"
                      onChange={(val) => setSelectedEventIdx(val)}
                      value={
                        selectedEventIdx !== null ? selectedEventIdx : undefined
                      }
                      options={testData.results.map((event, idx) => ({
                        label: `${event.Event}`,
                        value: idx,
                      }))}
                    />
                  </Form.Item>

                  {selectedEventIdx !== null &&
                    testData.results[selectedEventIdx] && (
                      <div>
                        <div style={{ marginBottom: 16 }}>
                          <p>
                            <strong>Day:</strong>{" "}
                            {testData.results[selectedEventIdx].Day}
                          </p>
                          <p>
                            <strong>Event Number:</strong>{" "}
                            {testData.results[selectedEventIdx].EventNum}
                          </p>
                          <p>
                            <strong>Start Time:</strong>{" "}
                            {testData.results[selectedEventIdx].Start}
                          </p>
                          <p>
                            <strong>Race Type:</strong>{" "}
                            {testData.results[selectedEventIdx].RaceType}
                          </p>
                          <p>
                            <strong>Status:</strong>{" "}
                            {testData.results[selectedEventIdx].Official
                              ? "Official"
                              : "Pending"}
                          </p>
                        </div>
                        <Table
                          dataSource={
                            testData.results[selectedEventIdx].DispOrder
                              ? testData.results[
                                  selectedEventIdx
                                ].DispOrder.map(
                                  (idx) =>
                                    testData.results[selectedEventIdx].entries[
                                      idx
                                    ],
                                )
                              : testData.results[selectedEventIdx].entries
                          }
                          columns={[
                            {
                              title: "Place",
                              dataIndex: "Place",
                              key: "Place",
                              width: 80,
                              render: (val) => val || "-",
                            },
                            {
                              title: "Crew",
                              dataIndex: "Crew",
                              key: "Crew",
                            },
                            {
                              title: "Abbreviation",
                              dataIndex: "CrewAbbrev",
                              key: "CrewAbbrev",
                              width: 100,
                            },
                            {
                              title: "Stroke",
                              dataIndex: "Stroke",
                              key: "Stroke",
                            },
                            {
                              title: "Bow",
                              dataIndex: "Bow",
                              key: "Bow",
                              width: 70,
                            },
                            {
                              title: "Adjusted Time",
                              dataIndex: "AdjTime",
                              key: "AdjTime",
                              width: 120,
                            },
                            {
                              title: "Raw Time",
                              dataIndex: "RawTime",
                              key: "RawTime",
                              width: 120,
                              render: (val) => val || "-",
                            },
                            {
                              title: "Penalty Code",
                              dataIndex: "PenaltyCode",
                              key: "PenaltyCode",
                              render: (val) => val || "-",
                            },
                          ]}
                          rowKey={(record) =>
                            `${selectedEventIdx}-${record.Index}`
                          }
                          size="small"
                          pagination={false}
                        />
                      </div>
                    )}
                </>
              ) : (
                <Empty description="No events found" />
              )}
            </div>
          ) : (
            <Badge
              status="error"
              text="Invalid CrewTimer ID - No data available"
            />
          )
        ) : (
          <Empty description="Click 'Fetch Test Data' to load events" />
        )}
      </Card>

      <Card
        title="Live Regatta Results"
        className="p-4 rounded-2xl shadow-sm"
        style={{ marginTop: 24 }}
      >
        <div style={{ marginBottom: 16 }}>
          {isConnected ? (
            <Badge
              status="success"
              text="🟢 Connected to Live Data"
              style={{ fontSize: 14 }}
            />
          ) : (
            <Badge
              status="error"
              text="🔴 Disconnected from Live Data"
              style={{ fontSize: 14 }}
            />
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <p>
            <strong>Connected ID:</strong> {config.CrewTimerID || "Not set"}
          </p>
          <p>
            <strong>Messages received:</strong> {liveMessages.length}
          </p>
          {liveMessages.length > 0 && (
            <Button onClick={() => setLiveMessages([])}>Clear Messages</Button>
          )}
        </div>

        {socketError && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              backgroundColor: "#fff2f0",
              border: "1px solid #ffccc7",
              borderRadius: 4,
              color: "#ff4d4f",
            }}
          >
            Error: {socketError}
          </div>
        )}

        {liveMessages.length > 0 ? (
          <div>
            {liveMessages.map((msg) => (
              <Card
                key={msg.id}
                type="inner"
                title={`Message at ${msg.timestamp}`}
                style={{ marginBottom: 12 }}
              >
                <div
                  style={{
                    backgroundColor: "#fafafa",
                    padding: 12,
                    borderRadius: 4,
                    maxHeight: 300,
                    overflow: "auto",
                    fontSize: 12,
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {JSON.stringify(msg.data, null, 2)}
                  </pre>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Empty description="Waiting for live data..." />
        )}
      </Card>
    </div>
  );
}
